import { LanguageSwitcher } from '@/i18n/LanguageSwitcher';
import { getUiLocale, t, useUiLocale } from '@/i18n/locale';
import { CourseMarkdown } from '@/components/CourseMarkdown';
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { MoreHorizontal } from 'lucide-react';
import { useCompanion } from './CompanionContext';
import { companionError } from './CompanionSession';
import {
  CompanionReadReply,
  CompanionRecordButton,
  CompanionVoiceOptions,
} from './CompanionVoiceControls';
import { BuddyAvatar } from './BuddyAvatar';
import { BuddyRoomGuide } from './BuddyRoomGuide';
import { MemoryDialog } from './CompanionDialogs';
import type { BuddyMemory, BuddyPageContext } from './companionApi';
import './companion.css';

export function MemoryShelf({ onClose }: { onClose?: () => void }) {
  useUiLocale();
  const session = useCompanion(),
    [editing, setEditing] = useState<{ text: string; memory?: BuddyMemory } | null>(null),
    [error, setError] = useState(''),
    [saving, setSaving] = useState(false);
  const forget = async (id: string) => {
    if (!session.snapshot || saving) return;
    setSaving(true);
    setError('');
    try {
      session.update(
        await session.client.forget(
          session.kidId,
          session.snapshot.revision,
          id,
          session.audience === 'parent',
        ),
      );
    } catch (caught) {
      setError(companionError(caught));
    } finally {
      setSaving(false);
    }
  };
  return (
    <section className="companion-memories">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold">{t('Things your buddy remembers')}</h2>
          <p className="mt-2 text-sm leading-relaxed text-slate2">
            {' '}
            {t('Favourites and little goals. You can edit or forget any of them.')}{' '}
          </p>
        </div>
        {onClose && (
          <button onClick={onClose} className="btn-pill-ghost" aria-label={t('Close memories')}>
            ×
          </button>
        )}
      </div>
      {!session.snapshot?.memory_enabled && (
        <p className="mt-4 rounded-2xl bg-wash-sunshine p-4 text-sm">
          {session.audience === 'parent'
            ? t('Saved memories are off. Switch them on when you are ready.')
            : t('Ask a grown-up to enable saved memories. You can still chat.')}
        </p>
      )}
      <ul className="mt-4 divide-y divide-hairline">
        {session.snapshot?.memories.map((memory) => (
          <li key={memory.id} className="py-4">
            <p className="text-sm leading-relaxed">{memory.text}</p>
            <small className="mt-2 block text-slate2">
              {memory.category === 'goal'
                ? t('Something to try')
                : memory.category === 'interest'
                  ? t('Interest')
                  : t('Preference')}{' '}
              · {memory.source === 'conversation' ? t('From our conversations') : t('Added by you')}
            </small>
            <div className="mt-3 flex gap-2">
              <button
                className="btn-pill-ghost"
                disabled={!session.snapshot?.memory_enabled || saving}
                onClick={() => setEditing({ text: memory.text, memory })}
              >
                {' '}
                {t('Edit')}{' '}
              </button>
              <button
                className="btn-pill-ghost"
                disabled={saving}
                onClick={() => void forget(memory.id)}
              >
                {' '}
                {t('Forget')}{' '}
              </button>
            </div>
          </li>
        ))}
      </ul>
      {session.snapshot?.memories.length === 0 && (
        <p className="my-6 text-sm text-slate2">
          {t(
            'As you chat, your buddy will get to know your interests and the things you are working on.',
          )}
        </p>
      )}
      {session.audience === 'parent' && (
        <button
          className="btn-pill-primary mt-5"
          disabled={!session.snapshot?.memory_enabled || saving}
          onClick={() => setEditing({ text: '' })}
        >
          {' '}
          {t('Add a memory')}{' '}
        </button>
      )}
      {error && (
        <p role="alert" className="mt-4 text-sm text-danger-600">
          {t(error)}
        </p>
      )}
      {editing && <MemoryDialog {...editing} onClose={() => setEditing(null)} />}
    </section>
  );
}
export function CompanionConversation({
  context,
  helper = false,
  tools,
}: {
  context: BuddyPageContext;
  helper?: boolean;
  tools?: ReactNode;
}) {
  useUiLocale();
  const session = useCompanion(),
    body = useRef<HTMLDivElement>(null);

  const scrollState = useRef<{ height: number; first?: string; count: number }>({
    height: 0,
    count: 0,
  });
  useLayoutEffect(() => {
    const node = body.current;
    if (!node) return;
    const previous = scrollState.current,
      first = session.messages[0]?.id;
    const prepended =
      !!previous.first &&
      first !== previous.first &&
      session.messages.some((m) => m.id === previous.first);
    if (prepended) node.scrollTop += node.scrollHeight - previous.height;
    else if (!previous.count || previous.height - node.scrollTop - node.clientHeight < 80)
      node.scrollTop = node.scrollHeight;
    scrollState.current = { height: node.scrollHeight, first, count: session.messages.length };
  }, [session.messages, session.busy, session.error]);
  useEffect(() => {
    const node = body.current;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      scrollState.current.height = node.scrollHeight;
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [session.loading, session.loadError]);
  if (session.loading)
    return (
      <div className="companion-loading" role="status">
        {' '}
        {t('Getting your buddy ready…')}{' '}
      </div>
    );
  if (session.loadError || !session.snapshot)
    return (
      <div className="companion-loading">
        <p>{t('Your buddy could not connect just now.')}</p>
        <button className="btn-pill-primary mt-4" onClick={() => void session.refresh()}>
          {' '}
          {t('Try again')}{' '}
        </button>
      </div>
    );
  return (
    <section
      className={'companion-conversation ' + (helper ? 'is-page-help' : '')}
      aria-label={helper ? t('Help with this page') : t('Companion chat')}
    >
      <div className="companion-conversation-head">
        <div className="companion-header-identity">
          <div className="companion-mobile-avatar">
            <BuddyAvatar
              profile={session.profile}
              paused={session.paused}
              className="h-full w-full"
            />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              {!helper && (
                <Link
                  to={session.returnTo}
                  state={session.returnState}
                  className="companion-back-link text-xs font-semibold text-ink"
                >
                  {' '}
                  {session.returnTo.includes('/class')
                    ? t('← Back to class')
                    : session.returnTo === '/learn'
                      ? t('← Learn')
                      : t('← Back to page')}{' '}
                </Link>
              )}
              <h1 className="font-bold">{session.profile.name}</h1>
            </div>
            <p className="mt-1 text-xs text-slate2">
              {session.priceUnavailable
                ? t('Your buddy')
                : helper
                  ? t('A little help with this page')
                  : t('Here to chat, at your pace.')}
            </p>
          </div>
        </div>
        {!helper && (
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            {tools}
            <button className="btn-pill-ghost" onClick={session.togglePause}>
              {session.paused ? t('Continue') : t('Pause')}
            </button>
          </div>
        )}
      </div>
      {helper && (
        <div className="companion-page-context">
          <small>{t('Looking at')}</small>
          <p>{context.title || t('This page')}</p>
          {context.summary && <p className="mt-1">{context.summary.slice(0, 240)}</p>}
        </div>
      )}
      <div
        ref={body}
        className="companion-message-list"
        role="log"
        aria-label={t('Conversation')}
        aria-live="polite"
      >
        {session.hasEarlier && (
          <div className="mb-4 text-center">
            <button
              type="button"
              className="btn-pill-ghost"
              disabled={session.loadingEarlier}
              onClick={() => {
                if (body.current) scrollState.current.height = body.current.scrollHeight;
                void session.loadEarlier();
              }}
            >
              {session.loadingEarlier ? t('Loading earlier messages…') : t('Earlier messages')}
            </button>
          </div>
        )}
        {session.earlierError && (
          <p role="alert" className="mb-4 text-sm text-danger-600">
            {t(session.earlierError)}
          </p>
        )}
        {session.messages.length === 0 && session.price !== null && !session.priceUnavailable && (
          <article className="buddy-message">
            <small>{session.profile.name}</small>
            <p>
              {helper
                ? t('What would you like a little help with?')
                : t(
                    'Hi! We can talk about your day, imagine something, or just take a quiet moment.',
                  )}
            </p>
          </article>
        )}
        {session.messages.map((message, index) => (
          <article
            key={index}
            className={'buddy-message ' + (message.role === 'user' ? 'from-child' : '')}
          >
            <small>{message.role === 'user' ? t('You') : session.profile.name}</small>
            {message.role === 'assistant' ? (
              <div className="buddy-markdown">
                <CourseMarkdown text={message.content} allowImages={false} />
              </div>
            ) : (
              <p>{message.content}</p>
            )}
            {message.role === 'assistant' && <CompanionReadReply message={message} />}
          </article>
        ))}
        {session.busy && (
          <p role="status" className="px-4 text-sm text-slate2">
            {' '}
            {t('Thinking about your idea…')}{' '}
          </p>
        )}
        {session.paused && (
          <p role="status" className="rounded-2xl bg-wash-sunshine p-4 text-sm">
            {' '}
            {t('We can pause. There is nothing you need to finish.')}{' '}
          </p>
        )}
      </div>
      {session.syncError && (
        <p role="status" className="px-4 py-2 text-xs text-slate2">
          {t(session.syncError)}{' '}
          <button type="button" className="underline" onClick={() => void session.syncHistory()}>
            {' '}
            {t('Check for new messages')}{' '}
          </button>
        </p>
      )}
      {session.error && (
        <div role="alert" className="companion-error">
          {t(session.error)}{' '}
          <button onClick={() => void session.refresh()} className="underline">
            {' '}
            {t('Refresh details')}{' '}
          </button>
        </div>
      )}
      <form
        className="companion-composer"
        onSubmit={(event) => {
          event.preventDefault();
          void session.send(session.draft, context);
        }}
      >
        {helper && (
          <div className="mb-2 flex gap-2">
            <button
              type="button"
              className="btn-pill-ghost"
              disabled={session.paused || session.busy || session.price === null}
              onClick={() =>
                void session.send(t('Give me one small hint about this page.'), context)
              }
            >
              {' '}
              {t('One small hint')}{' '}
            </button>
            <Link
              to="/learn/companion"
              className="btn-pill-ghost"
              onClick={() => session.setHelpOpen(false)}
            >
              {' '}
              {t('Open chat room ↗')}{' '}
            </Link>
          </div>
        )}
        <div className="flex items-end gap-2">
          <CompanionRecordButton />
          <textarea
            aria-label={t('Tell your buddy')}
            placeholder={session.paused ? t('Take your time.') : t('What is on your mind?')}
            value={session.draft}
            onChange={(event) => session.setDraft(event.target.value)}
            maxLength={500}
            rows={2}
            disabled={session.paused || session.busy}
            className="input min-h-14 flex-1 resize-none"
          />
          {session.busy ? (
            <button type="button" className="btn-pill-ghost" onClick={session.stop}>
              {' '}
              {t('Stop')}{' '}
            </button>
          ) : (
            <button
              className="btn-pill-primary"
              disabled={session.paused || !session.draft.trim() || session.price === null}
            >
              {' '}
              {t('Send')}{' '}
              {session.price !== null && session.price > 0
                ? ' · ' +
                  session.price +
                  t(' Star') +
                  (getUiLocale() === 'en' && session.price !== 1 ? 's' : '')
                : ''}
            </button>
          )}
        </div>
        {session.price === null && (
          <p className="mt-2 text-xs text-slate2">
            {session.priceUnavailable
              ? t('Chat is unavailable right now.')
              : t('Connecting to your buddy…')}{' '}
            <button type="button" className="underline" onClick={() => void session.refresh()}>
              {' '}
              {t('Try again')}{' '}
            </button>
          </p>
        )}
      </form>
    </section>
  );
}
export function CompanionRoom() {
  useUiLocale();
  const session = useCompanion(),
    [voiceOpen, setVoiceOpen] = useState(false),
    [tour, setTour] = useState(false);
  const needsSetup = !!session.snapshot && !session.snapshot.profile;
  const navigate = useNavigate();
  const location = useLocation();
  const designPath =
    location.pathname.startsWith('/try/') || location.pathname.endsWith('companion-preview.html')
      ? '?view=design'
      : '/learn/companion/design';
  if (needsSetup) return <Navigate to={designPath} replace />;
  return (
    <div className="buddy-room">
      <aside className="buddy-room-sidebar">
        <div className={'buddy-room-scene light-' + session.profile.room}>
          <BuddyAvatar
            profile={session.profile}
            paused={session.paused}
            className="h-full w-full"
          />
        </div>
        <button
          className="btn-pill-primary mt-4 w-full"
          onClick={() => navigate(designPath)}
          disabled={!session.snapshot}
        >
          {needsSetup ? t('Set up my buddy') : t('Dress my buddy')}
        </button>
        <button className="btn-pill-ghost mt-3 w-full" onClick={() => setTour(true)}>
          {' '}
          {t('Show me around')}{' '}
        </button>
        <button className="btn-pill-ghost mt-3 w-full" onClick={() => setVoiceOpen(true)}>
          {' '}
          {t('Voice and recording')}{' '}
        </button>
        <Link to="/learn/projects" className="mt-5 block text-center text-sm text-slate2">
          {' '}
          {t('Our creations ↗')}{' '}
        </Link>
      </aside>
      <CompanionConversation
        context={{ page: 'home' }}
        tools={
          <details className="buddy-room-tools">
            <summary aria-label={t('Buddy options')}>
              <MoreHorizontal size={21} aria-hidden="true" />
            </summary>
            <div>
              <Link
                to={session.returnTo}
                state={session.returnState}
                className="btn-pill-ghost w-full"
              >
                {' '}
                {session.returnTo.includes('/class')
                  ? t('← Back to class')
                  : session.returnTo === '/learn'
                    ? t('← Learn')
                    : t('← Back to page')}{' '}
              </Link>
              <button
                type="button"
                className="btn-pill-ghost"
                onClick={(event) => {
                  event.currentTarget.closest('details')?.removeAttribute('open');
                  setTour(true);
                }}
              >
                {' '}
                {t('Show me around')}{' '}
              </button>
              <button
                type="button"
                className="btn-pill-ghost"
                onClick={(event) => {
                  event.currentTarget.closest('details')?.removeAttribute('open');
                  setVoiceOpen(true);
                }}
              >
                {' '}
                {t('Voice and recording')}{' '}
              </button>
              <button
                type="button"
                className="btn-pill-ghost"
                onClick={(event) => {
                  event.currentTarget.closest('details')?.removeAttribute('open');
                  navigate(designPath);
                }}
              >
                {' '}
                {t('Dress my buddy')}{' '}
              </button>
            </div>
          </details>
        }
      />
      {voiceOpen && <CompanionVoiceOptions onClose={() => setVoiceOpen(false)} />}
      {tour && (
        <BuddyRoomGuide
          onClose={() => setTour(false)}
          onDress={() => {
            setTour(false);
            navigate(designPath);
          }}
        />
      )}
    </div>
  );
}
export function CompanionHelp() {
  useUiLocale();
  const session = useCompanion();
  const latest = useRef(session);
  latest.current = session;
  useEffect(() => {
    if (!session.helpOpen) return;
    const prior = document.activeElement;
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        latest.current.stop();
        latest.current.setHelpOpen(false);
      }
    };
    window.addEventListener('keydown', close);
    return () => {
      window.removeEventListener('keydown', close);
      if (prior instanceof HTMLElement && prior.isConnected) prior.focus();
    };
  }, [session.helpOpen]);
  if (!session.snapshot?.profile || !session.page || session.page.page === 'home') return null;
  return (
    <aside className="companion-help">
      <button
        className="companion-launcher"
        onClick={() => {
          if (session.helpOpen) session.stop();
          session.setHelpOpen(!session.helpOpen);
        }}
        aria-expanded={session.helpOpen}
        aria-controls="companion-page-help"
      >
        ✦ {session.profile.name} {t('can help')}{' '}
      </button>
      {session.helpOpen && (
        <div id="companion-page-help" className="companion-help-panel">
          <button
            className="companion-close-help btn-pill-ghost"
            aria-label={t('Close page help')}
            onClick={() => {
              session.stop();
              session.setHelpOpen(false);
            }}
          >
            ×
          </button>
          <CompanionConversation context={session.page} helper />
        </div>
      )}
    </aside>
  );
}
